import { type PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { getAllClients, getClientsPaged } from "../service/client.service";
import type { RootState } from "../store";
import type { TClientForm, TClientScope, Tdsm } from "../types/index.type";
import { sortClientsByName } from "../utils/datamanipulation.utils";

export const SIDEBAR_PAGE_SIZE = 50;

// Legacy full fetch — now only hydrates the byId cache (e.g. refresh after a share). No array writes.
export const fetchAllClients = createAsyncThunk("client/fetchAllClients", async (_, { rejectWithValue }) => {
  try {
    const response = await getAllClients();
    const ownedClients = sortClientsByName(response.data.owned || []);
    const sharedClients = sortClientsByName(response.data.shared || []);
    return { ownedClients, sharedClients };
  } catch (error) {
    return rejectWithValue(error);
  }
});

type SidebarScope = Exclude<TClientScope, "all">;

// Server-paginated sidebar section fetch (owned OR shared). Drives infinite scroll + search.
export const fetchClientSidebarPage = createAsyncThunk(
  "client/fetchSidebarPage",
  async (args: { scope: SidebarScope; offset: number; search: string; reset: boolean }, { rejectWithValue }) => {
    try {
      const { scope, offset, search } = args;
      const response = await getClientsPaged({ scope, offset, search, page_size: SIDEBAR_PAGE_SIZE });
      return { ...args, ...response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

type SidebarSection = { ids: number[]; offset: number; hasMore: boolean; loading: boolean; error: boolean };
const emptySection = (): SidebarSection => ({ ids: [], offset: 0, hasMore: false, loading: false, error: false });

// Merge clients into the byId display cache (never the source of truth for existence — only hydration).
const indexClients = (byId: Record<number, TClientForm>, list: TClientForm[]) => {
  for (const c of list) {
    if (c?.id != null) byId[c.id] = c;
  }
};

const idsOf = (list: TClientForm[]): number[] => list.map((c) => c.id).filter((id): id is number => id != null);

// Define a type for the slice state
type ClientState = {
  // Normalized display cache + paginated sidebar state (replaced the old in-memory list arrays).
  byId: Record<number, TClientForm>;
  recentIds: number[];
  sidebar: { query: string; owned: SidebarSection; shared: SidebarSection };
  clientForm: TClientForm;
  dsm: Tdsm[];
  customizationList: any[];
  note_type_instructions: {
    [key: number]: string | { note: string; is_system: boolean };
  };
  suggestion: string;
  isEditing: boolean;
};

// Initial state
const initialState: ClientState = {
  byId: {},
  recentIds: [],
  sidebar: { query: "", owned: emptySection(), shared: emptySection() },
  dsm: [],
  clientForm: {
    name: "",
    category_type: 0,
    gender_type: 0,
    language_type: 0,
    diagnosis_ids: [],
    refer_type: 0,
    mrn: "",
    date_of_birth: null,
    context: "",
  },
  customizationList: [],
  note_type_instructions: {},
  suggestion: "",
  isEditing: false,
};

// Slice
const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    // Merge clients into the byId hydration cache.
    cacheClients: (state, action: PayloadAction<TClientForm[]>) => {
      indexClients(state.byId, action.payload);
    },
    // Recently-used clients for picker empty-focus (derived from recent notes' clients).
    setRecentClients: (state, action: PayloadAction<TClientForm[]>) => {
      indexClients(state.byId, action.payload);
      state.recentIds = idsOf(action.payload);
    },
    setSidebarQuery: (state, action: PayloadAction<string>) => {
      state.sidebar.query = action.payload;
    },
    setClientForm: (state, action: PayloadAction<TClientForm>) => {
      state.clientForm = action.payload;
    },
    setDsm: (state, action: PayloadAction<{ id: number; code: string; name: string }[]>) => {
      state.dsm = action.payload;
    },
    setCustomizationList: (state, action: PayloadAction<any[]>) => {
      state.customizationList = action.payload;
    },
    setNoteTypeInstructionNote: (
      state,
      action: PayloadAction<{
        [key: number]: string | { note: string; is_system: boolean };
      }>,
    ) => {
      state.note_type_instructions = action.payload;
    },
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    resetClientForm: (state) => {
      state.clientForm = { ...initialState.clientForm };
    },
    setSuggestion: (state, action: PayloadAction<string>) => {
      state.suggestion = action.payload;
    },
    clearSuggestion: (state) => {
      state.suggestion = "";
    },
    // Optimistic CRUD — patch byId + sidebar in place (no full-list refetch).
    addClient: (state, action: PayloadAction<TClientForm>) => {
      const newClient = action.payload;
      if (newClient.id != null) {
        state.byId[newClient.id] = newClient;
        if (!state.sidebar.owned.ids.includes(newClient.id)) {
          state.sidebar.owned.ids.unshift(newClient.id);
        }
      }
    },
    removeClient: (state, action: PayloadAction<number>) => {
      const clientId = action.payload;
      delete state.byId[clientId];
      state.sidebar.owned.ids = state.sidebar.owned.ids.filter((id) => id !== clientId);
      state.sidebar.shared.ids = state.sidebar.shared.ids.filter((id) => id !== clientId);
      state.recentIds = state.recentIds.filter((id) => id !== clientId);
    },
    updateClientInList: (state, action: PayloadAction<TClientForm>) => {
      const updatedClient = action.payload;
      if (updatedClient.id != null) {
        state.byId[updatedClient.id] = updatedClient;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllClients.fulfilled, (state, action) => {
      const { ownedClients, sharedClients } = action.payload;
      indexClients(state.byId, [...ownedClients, ...sharedClients]);
    });
    builder.addCase(fetchClientSidebarPage.pending, (state, action) => {
      const section = state.sidebar[action.meta.arg.scope];
      section.loading = true;
      section.error = false;
      if (action.meta.arg.reset) {
        section.ids = [];
        section.offset = 0;
        section.hasMore = false;
      }
    });
    builder.addCase(fetchClientSidebarPage.fulfilled, (state, action) => {
      const { scope, reset, results, hasMore, next_offset, offset } = action.payload;
      const section = state.sidebar[scope];
      indexClients(state.byId, results);
      const newIds = idsOf(results);
      section.ids = reset ? newIds : [...section.ids, ...newIds.filter((id) => !section.ids.includes(id))];
      section.offset = next_offset ?? offset + results.length;
      section.hasMore = hasMore;
      section.loading = false;
      section.error = false;
    });
    builder.addCase(fetchClientSidebarPage.rejected, (state, action) => {
      const section = state.sidebar[action.meta.arg.scope];
      section.loading = false;
      section.error = true;
    });
  },
});

// Actions
export const {
  setClientForm,
  cacheClients,
  setRecentClients,
  setSidebarQuery,
  resetClientForm,
  setDsm,
  setIsEditing,
  setNoteTypeInstructionNote,
  setCustomizationList,
  setSuggestion,
  clearSuggestion,
  addClient,
  removeClient,
  updateClientInList,
} = clientSlice.actions;

// Selectors
export const selectClientById =
  (id?: number | null) =>
  (state: RootState): TClientForm | undefined =>
    id != null ? state.client.byId[id] : undefined;

export const selectClientByUuid =
  (uuid?: string | null) =>
  (state: RootState): TClientForm | undefined =>
    uuid ? Object.values(state.client.byId).find((c) => c.uuid === uuid) : undefined;

// Memoized so it returns a stable array reference (avoids the "selector returns a new reference"
// re-render warning when consumed via useSelector).
export const selectRecentClients = createSelector(
  (state: RootState) => state.client.recentIds,
  (state: RootState) => state.client.byId,
  (recentIds, byId): TClientForm[] => recentIds.map((id) => byId[id]).filter((c): c is TClientForm => Boolean(c)),
);

export const selectSidebarSection =
  (scope: SidebarScope) =>
  (state: RootState): SidebarSection =>
    state.client.sidebar[scope];

export const selectSidebarQuery = (state: RootState): string => state.client.sidebar.query;

// Reducer
export default clientSlice.reducer;
